/**
 * AI panelinin durumu.
 *
 * Panel sekmelerden bağımsız: kullanıcı ekran değiştirirken sohbet açık kalıyor ve
 * akış kesilmiyor. Bu yüzden durum bileşende değil, kabukta yaşayan bir store'da.
 *
 * Akış İPTAL EDİLEBİLİR (`AbortController`): "durdur" düğmesi bağlantıyı kapatıyor,
 * sunucu tarafında da `RequestAborted` ile CLI süreci sonlanıyor — yarıda kalan yanıt
 * ekranda kalır, atılmaz.
 */
import { create } from 'zustand';
import type { AiAttachmentRef, AiQuota, ChatMessage, ReportArtifact } from '@/shared/types/aiChat';

/** Panel genişliği (masaüstü) — kullanıcı sürükleyerek değiştiriyor, kalıcı. */
const WIDTH_KEY = 'octo.web.ai.width';
export const MIN_WIDTH = 360;
export const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 460;

function storedWidth(): number {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= MIN_WIDTH && raw <= MAX_WIDTH) return raw;
  } catch {
    /* depolama kapalı olabilir */
  }
  return DEFAULT_WIDTH;
}

let seq = 0;
const nextId = () => `m${Date.now().toString(36)}${++seq}`;

export interface AiState {
  open: boolean;
  width: number;
  /** Sunucudaki Claude oturumu — sohbeti sürdürmek için gerekli. */
  sessionId: string | null;
  /** Kalıcı sohbet kaydı (AiConversations). */
  conversationId: string | null;
  messages: ChatMessage[];
  streaming: boolean;
  /** Akışta o an çalışan araç — "dosya okunuyor…" gibi bir durum satırı için. */
  activeTool: string | null;
  quota: AiQuota | null;
  quotaExceeded: boolean;
  pendingAttachments: AiAttachmentRef[];

  setOpen: (open: boolean) => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  newChat: () => void;
  openConversation: (conversationId: string, sessionId: string | null, messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateMessage: (id: string, patch: Partial<ChatMessage>) => void;
  appendText: (id: string, text: string) => void;
  addArtifact: (id: string, artifact: ReportArtifact) => void;
  setStreaming: (streaming: boolean) => void;
  setActiveTool: (tool: string | null) => void;
  setSession: (sessionId: string) => void;
  setConversation: (conversationId: string | null) => void;
  setQuota: (quota: AiQuota | null) => void;
  setPendingAttachments: (attachments: AiAttachmentRef[]) => void;
  reset: () => void;
}

export const useAiStore = create<AiState>((set, get) => ({
  open: false,
  width: storedWidth(),
  sessionId: null,
  conversationId: null,
  messages: [],
  streaming: false,
  activeTool: null,
  quota: null,
  quotaExceeded: false,
  pendingAttachments: [],

  setOpen: (open) => set({ open }),
  toggle: () => set({ open: !get().open }),
  setWidth: (width) => {
    const clamped = Math.min(Math.max(Math.round(width), MIN_WIDTH), MAX_WIDTH);
    try {
      localStorage.setItem(WIDTH_KEY, String(clamped));
    } catch {
      /* depolama kapalı olabilir */
    }
    set({ width: clamped });
  },

  newChat: () =>
    set({
      sessionId: null,
      conversationId: null,
      messages: [],
      activeTool: null,
      pendingAttachments: [],
    }),

  openConversation: (conversationId, sessionId, messages) =>
    set({ conversationId, sessionId, messages, activeTool: null, pendingAttachments: [] }),

  addMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),

  updateMessage: (id, patch) =>
    set((s) => ({ messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)) })),

  /*
   * Metin parçaları BOŞ SATIRLA ayrılır: Claude aynı yanıtta birden çok metin bloğu
   * gönderebiliyor (arada araç çağrısı olduğunda) ve bunları uç uca eklemek cümleleri
   * birbirine yapıştırıyordu.
   */
  appendText: (id, text) =>
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== id) return m;
        const current = m.content ?? '';
        const separator = current && !current.endsWith('\n\n') ? '\n\n' : '';
        return { ...m, content: current + separator + text };
      }),
    })),

  addArtifact: (id, artifact) =>
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== id) return m;
        // Aynı rapor birden çok kez yazılabiliyor; sonuncusu geçerli.
        const rest = (m.artifacts ?? []).filter((a) => a.fileName !== artifact.fileName);
        return { ...m, artifacts: [...rest, artifact] };
      }),
    })),

  setStreaming: (streaming) => set({ streaming, activeTool: streaming ? get().activeTool : null }),
  setActiveTool: (activeTool) => set({ activeTool }),
  setSession: (sessionId) => set({ sessionId }),
  setConversation: (conversationId) => set({ conversationId }),
  setQuota: (quota) => set({ quota, quotaExceeded: !!quota && !quota.unlimited && quota.remaining <= 0 }),
  setPendingAttachments: (pendingAttachments) => set({ pendingAttachments }),

  reset: () =>
    set({
      open: false,
      sessionId: null,
      conversationId: null,
      messages: [],
      streaming: false,
      activeTool: null,
      quota: null,
      quotaExceeded: false,
      pendingAttachments: [],
    }),
}));

export const newMessageId = nextId;
